/**
 * Plumb, ported from the single-file WebGL sketch to a 2D canvas that reads a
 * still picture instead of a camera. The overlays (tracery, detect, fields,
 * windows), the modes (photo, glyph, block), the tones, the words and the
 * glitch cut are Plumb's; the skin key is replaced by "what differs from the
 * picture's border", so the regions sit on the subject of any image. Depth and
 * edge are two more full-frame readings: depth from a precomputed depth map
 * when one is given, edge worked out from the picture itself.
 */

import { depthMap, edgeMap, frameOf, loadImage, paint } from "./gaze";
import type { Frame } from "./gaze";

export type Mode = "photo" | "glyph" | "block" | "depth" | "edge";
export type Overlay = "tracery" | "detect" | "fields" | "windows";

export interface PlumbOptions {
  src: string;
  /** A depth map of the same picture (near is bright). Without it depth falls back to brightness. */
  depth?: string;
  mode?: Mode | "auto";
  overlay?: Overlay | "auto" | "none";
  /** Object-position of the picture, 0..1. */
  fx?: number;
  fy?: number;
  /** How many tracked regions. */
  nodes?: number;
  /** 1 is Plumb's own restless cut rate; lower is calmer. */
  tempo?: number;
  maxWidth?: number;
  palette?: PaletteName;
}

export interface PlumbHandle {
  set(patch: { mode?: Mode | "auto"; overlay?: Overlay | "auto" | "none" }): void;
  destroy(): void;
}

interface Region {
  id: number;
  label: string;
  cx: number;
  cy: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Node {
  i: number;
  x: number;
  y: number;
  bw: number;
  bh: number;
  on: boolean;
  life: number;
  wl: number;
  word: string;
}

const GN = 54;
const TN = 30;
const HZ = 30;
const WORDS = (
  "WARP WEFT KNOT LOOM THREAD SELVEDGE PILE SHED HEDDLE SPINDLE SHUTTLE FRINGE DYE MADDER INDIGO SAFFRON WALNUT " +
  "POMEGRANATE BORDER MEDALLION FIELD MOTIF AMULET COMB STAR HOOK BURDOCK PALM WRIST KNUCKLE NAIL FINGER THUMB LIFELINE " +
  "GESTURE HOLD RELEASE REACH GRASP TOUCH SIGNAL NOISE MEMORY PATTERN REPEAT MIRROR TENSION SLACK UNRAVEL MEND"
).split(" ");
const TONES = ["#ff4fa3", "#b7ff2a", "#3d6bff", "#ff6a1f"];
const TONE_TEXT = ["#000", "#000", "#fff", "#000"];
export const PALETTES = {
  /** Plumb's own: ink ground with lime glyphs, lilac ground with green cells. */
  plumb: {
    glyphGround: "#11131c",
    glyph: "#d6ff3f",
    blockGround: "#c9b8ed",
    block: "#10402f",
    blockEdge: "rgba(41,26,64,.55)",
    blockInk: "#ff6a1f",
    scanGround: "#11131c",
    scan: "#fff5e0",
  },
  /** Acid digital: black and acid green only. */
  acid: {
    glyphGround: "#050505",
    glyph: "#d7ff00",
    blockGround: "#d7ff00",
    block: "#050505",
    blockEdge: "rgba(5,5,5,.5)",
    blockInk: "#3d6bff",
    scanGround: "#050505",
    scan: "#d7ff00",
  },
  /** Black and off-white: depth reads as a plain depth map. */
  mono: {
    glyphGround: "#050505",
    glyph: "#f2f2ee",
    blockGround: "#f2f2ee",
    block: "#050505",
    blockEdge: "rgba(5,5,5,.5)",
    blockInk: "#ff6a1f",
    scanGround: "#050505",
    scan: "#f2f2ee",
  },
  /** Ink on lilac. */
  lilac: {
    glyphGround: "#c9b8ed",
    glyph: "#050505",
    blockGround: "#c9b8ed",
    block: "#050505",
    blockEdge: "rgba(5,5,5,.5)",
    blockInk: "#ff6a1f",
    scanGround: "#c9b8ed",
    scan: "#050505",
  },
};
export type PaletteName = keyof typeof PALETTES;

const OVERLAYS: Overlay[] = ["tracery", "detect", "fields", "windows"];
const MONO = '"Fragment Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

const rnd = Math.random;
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const pick = <T>(a: T[]): T => a[(rnd() * a.length) | 0];
const hash = (a: number, b = 0) => {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
};

type Ctx = CanvasRenderingContext2D;

/** Plumb's eight glyphs: × · / = ° < | - */
function glyph(x: Ctx, k: number, ox: number, oy: number, s: number) {
  const line = (...p: number[]) => {
    x.beginPath();
    x.moveTo(ox + p[0] * s, oy + p[1] * s);
    for (let i = 2; i < p.length; i += 2) x.lineTo(ox + p[i] * s, oy + p[i + 1] * s);
    x.stroke();
  };
  if (k === 0) {
    line(0.31, 0.31, 0.69, 0.69);
    line(0.69, 0.31, 0.31, 0.69);
  } else if (k === 1) x.fillRect(ox + 0.44 * s, oy + 0.44 * s, 0.125 * s, 0.125 * s);
  else if (k === 2) line(0.69, 0.25, 0.31, 0.75);
  else if (k === 3) {
    line(0.28, 0.39, 0.72, 0.39);
    line(0.28, 0.61, 0.72, 0.61);
  } else if (k === 4) {
    x.beginPath();
    x.arc(ox + 0.5 * s, oy + 0.41 * s, 0.125 * s, 0, 6.3);
    x.stroke();
  } else if (k === 5) line(0.66, 0.28, 0.34, 0.5, 0.66, 0.72);
  else if (k === 6) line(0.5, 0.22, 0.5, 0.78);
  else line(0.28, 0.5, 0.72, 0.5);
}

function labelBar(
  x: Ctx,
  W: number,
  H: number,
  text: string,
  px: number,
  py: number,
  size: number,
  fill: string,
  color: string
) {
  x.font = `600 ${size}px ${MONO}`;
  const pad = size * 0.55;
  const h = Math.round(size * 1.5);
  const w = Math.ceil(x.measureText(text).width + pad * 2);
  const left = clamp(px, 0, W - w);
  const top = clamp(py, 0, H - h);
  x.fillStyle = fill;
  x.fillRect(left, top, w, h);
  x.fillStyle = color;
  x.textBaseline = "middle";
  x.fillText(text, left + pad, top + h / 2 + size * 0.04);
}

function cursor(x: Ctx, px: number, py: number, s: number) {
  x.save();
  x.translate(px, py);
  x.scale(s, s);
  x.beginPath();
  x.moveTo(0, 0);
  x.lineTo(0, 14);
  x.lineTo(3.4, 10.8);
  x.lineTo(5.6, 16);
  x.lineTo(7.8, 15);
  x.lineTo(5.6, 10);
  x.lineTo(10, 10);
  x.closePath();
  x.fillStyle = "#fff";
  x.fill();
  x.lineWidth = 1;
  x.lineJoin = "round";
  x.strokeStyle = "#000";
  x.stroke();
  x.restore();
}

/** Measuring grid and rulers, a circle in a square per region, straight lines between the centres. */
function drawTracery(x: Ctx, R: Region[], W: number, H: number, u: number, ink: string) {
  const step = W / 12;
  x.strokeStyle = x.fillStyle = ink;
  x.lineWidth = u;
  x.setLineDash([]);
  x.globalAlpha = 0.16;
  x.beginPath();
  for (let p = step; p < W; p += step) {
    x.moveTo(Math.round(p) + 0.5, 0);
    x.lineTo(Math.round(p) + 0.5, H);
  }
  for (let p = step; p < H; p += step) {
    x.moveTo(0, Math.round(p) + 0.5);
    x.lineTo(W, Math.round(p) + 0.5);
  }
  x.stroke();
  x.globalAlpha = 0.7;
  x.font = `${10 * u}px ${MONO}`;
  x.textBaseline = "top";
  x.beginPath();
  for (let i = 0; (i * W) / 48 <= Math.max(W, H); i++) {
    const p = (i / 48) * W;
    const len = (i % 4 ? 4 : 9) * u;
    if (p <= W) {
      x.moveTo(p, 0);
      x.lineTo(p, len);
    }
    if (p <= H) {
      x.moveTo(0, p);
      x.lineTo(len, p);
    }
    if (i % 8 === 0 && i) {
      if (p < W - 20 * u) x.fillText(String(i * 40), p + 3 * u, 10 * u);
      if (p < H - 20 * u) x.fillText(String(i * 40), 11 * u, p + 3 * u);
    }
  }
  x.stroke();
  if (!R.length) {
    x.globalAlpha = 1;
    return;
  }
  const P = R.map(r => [r.cx * W, r.cy * H]);

  x.globalAlpha = 0.35;
  x.setLineDash([3 * u, 5 * u]);
  x.beginPath();
  for (const p of P.slice(0, 3)) {
    x.moveTo(p[0], p[1]);
    x.lineTo(p[0], 0);
    x.moveTo(p[0], p[1]);
    x.lineTo(0, p[1]);
  }
  x.stroke();

  x.globalAlpha = 0.9;
  x.setLineDash([]);
  x.lineWidth = 1.3 * u;
  x.beginPath();
  x.moveTo(P[0][0], P[0][1]);
  for (const p of P.slice(1)) x.lineTo(p[0], p[1]);
  x.stroke();
  if (P.length > 1) {
    const e = P[P.length - 1];
    const f = P[P.length - 2];
    const a = Math.atan2(e[1] - f[1], e[0] - f[0]);
    x.beginPath();
    x.moveTo(e[0], e[1]);
    x.lineTo(e[0] - Math.cos(a - 0.4) * 11 * u, e[1] - Math.sin(a - 0.4) * 11 * u);
    x.lineTo(e[0] - Math.cos(a + 0.4) * 11 * u, e[1] - Math.sin(a + 0.4) * 11 * u);
    x.fill();
  }
  for (const r of R) {
    const px = r.cx * W;
    const py = r.cy * H;
    const rad = Math.max(r.w * W, r.h * H) / 2;
    x.globalAlpha = 0.75;
    x.lineWidth = u;
    x.setLineDash([]);
    x.beginPath();
    x.arc(px, py, rad, 0, 6.3);
    x.stroke();
    x.strokeRect(px - rad, py - rad, rad * 2, rad * 2);
    x.globalAlpha = 1;
    x.fillRect(px - 3 * u, py - 3 * u, 6 * u, 6 * u);
    x.beginPath();
    x.arc(px, py, 8 * u, 0, 6.3);
    x.stroke();
    x.font = `${9 * u}px ${MONO}`;
    x.textBaseline = "bottom";
    x.fillText(`${Math.round(r.w * 1920)}x${Math.round(r.h * 1920)}`, px - rad + 4 * u, py - rad - 3 * u);
    x.fillText(r.label, px + 11 * u, py - 5 * u);
  }
}

/** Corner brackets, crosshair and a caption bar per region; in bursts the boxes fill solid. */
function drawDetect(x: Ctx, R: Region[], W: number, H: number, u: number, simT: number) {
  const beat = Math.floor(simT / 0.8);
  const blocked = hash(beat, 11) < 0.3;
  const size = Math.max(7, Math.round(10 * u));
  for (const r of R) {
    const tone = r.id % 4;
    const bx = r.x * W;
    const by = r.y * H;
    const bw = r.w * W;
    const bh = r.h * H;
    const px = r.cx * W;
    const py = r.cy * H;
    const text = `${r.id}. ${r.label}`;
    const solid = blocked && hash(beat, r.id) < 0.85;
    if (solid) {
      x.fillStyle = TONES[tone];
      x.fillRect(bx, by, bw, bh);
    } else {
      const arm = clamp(Math.min(bw, bh) * 0.2, 5 * u, 26 * u);
      x.strokeStyle = TONES[tone];
      x.lineWidth = 1.8 * u;
      x.beginPath();
      for (const [qx, qy, sx, sy] of [
        [bx, by, 1, 1],
        [bx + bw, by, -1, 1],
        [bx, by + bh, 1, -1],
        [bx + bw, by + bh, -1, -1],
      ]) {
        x.moveTo(qx + sx * arm, qy);
        x.lineTo(qx, qy);
        x.lineTo(qx, qy + sy * arm);
      }
      x.stroke();
    }
    x.strokeStyle = solid ? TONE_TEXT[tone] : "rgba(255,255,255,.92)";
    x.lineWidth = 1.2 * u;
    x.beginPath();
    x.moveTo(px - 9 * u, py);
    x.lineTo(px + 9 * u, py);
    x.moveTo(px, py - 9 * u);
    x.lineTo(px, py + 9 * u);
    x.stroke();
    if (solid) {
      x.font = `600 ${size * 0.85}px ${MONO}`;
      x.fillStyle = TONE_TEXT[tone];
      x.textBaseline = "top";
      x.fillText(text, bx + size * 0.5, by + size * 0.4, bw - size);
    } else labelBar(x, W, H, text, bx, by - size * 1.5 - 2 * u, size, TONES[tone], TONE_TEXT[tone]);
  }
}

/** The normal-map patches are drawn by the caller; here only the clusters of small tags. */
function drawFields(x: Ctx, R: Region[], W: number, H: number, u: number) {
  const size = Math.max(5, Math.round(7 * u));
  for (const r of R) {
    const tags = 1 + Math.floor(hash(r.id, 25) * 4);
    for (let k = 0; k < tags; k++) {
      const tone = (r.id + k) % 4;
      const px = r.x * W + (k ? (hash(r.id, 26 + k) - 0.2) * r.w * W : 0);
      const py = r.y * H + (k ? hash(r.id, 30 + k) * r.h * H : -size * 1.6);
      labelBar(x, W, H, k ? r.label.slice(0, 4) : `${r.id}. ${r.label}`, px, py, size, TONES[tone], TONE_TEXT[tone]);
    }
  }
}

/** Each box is a pane with a title strip and a pointer, and a loose flock of pointers circles them. */
function drawWindows(x: Ctx, R: Region[], W: number, H: number, u: number, simT: number) {
  const size = Math.max(5, Math.round(6.5 * u));
  for (const r of R) {
    const bx = r.x * W;
    const by = r.y * H;
    const bw = r.w * W;
    const bh = r.h * H;
    x.strokeStyle = "rgba(255,255,255,.8)";
    x.lineWidth = u;
    x.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
    labelBar(x, W, H, `${r.id}. ${r.label}`, bx, by - size * 1.5, size, "rgba(18,18,24,.8)", "#fff");
    cursor(x, bx + bw * (0.4 + hash(r.id, 51) * 0.25), by + bh * (0.4 + hash(r.id, 52) * 0.25), 1.7 * u);
  }
  if (!R.length) return;
  const beat = Math.floor(simT / 0.7);
  const reach = Math.min(W, H);
  for (let k = 0; k < 12; k++) {
    if (hash(k, beat) > 0.7) continue;
    const home = R[Math.floor(hash(k, 54) * R.length)];
    const a = simT * (0.6 + hash(k, 56)) + hash(k, 57) * 6.283;
    const rad = (0.04 + hash(k, 55) * 0.16) * reach;
    cursor(x, home.cx * W + Math.cos(a) * rad, home.cy * H + Math.sin(a) * rad, 1.2 * u);
  }
}

/** One pane redrawn as a ridge scan: each row is a line lifted by the brightness under it. */
function drawScan(
  x: Ctx,
  r: Region,
  W: number,
  H: number,
  u: number,
  simT: number,
  lum: Frame,
  ground: string,
  ink: string
) {
  const bx = clamp(r.x, 0, 1) * W;
  const by = clamp(r.y, 0, 1) * H;
  const bw = Math.min(r.x + r.w, 1) * W - bx;
  const bh = Math.min(r.y + r.h, 1) * H - by;
  if (bw < 8 || bh < 8) return;
  const rows = Math.max(4, Math.round(bh / (6 * u)));
  const rowH = bh / rows;
  const cols = Math.max(8, Math.round(bw / (4 * u)));
  const lift = rowH * 3.2;
  const live = Math.floor(simT * 9 + r.id * 5) % (rows + 6);
  const at = (px: number, py: number) => {
    const sx = clamp(Math.round((px / W) * lum.w), 0, lum.w - 1);
    const sy = clamp(Math.round((py / H) * lum.h), 0, lum.h - 1);
    return lum.lum[sy * lum.w + sx];
  };
  x.save();
  x.beginPath();
  x.rect(bx, by, bw, bh);
  x.clip();
  x.globalAlpha = 1;
  x.fillStyle = ground;
  x.fillRect(bx, by, bw, bh);
  x.lineWidth = u;
  x.lineJoin = "round";
  for (let j = 0; j <= rows; j++) {
    const base = by + j * rowH;
    x.beginPath();
    for (let i = 0; i <= cols; i++) {
      const px = bx + (i / cols) * bw;
      const py = base - at(px, base) * lift;
      if (i) x.lineTo(px, py);
      else x.moveTo(px, py);
    }
    // each row hides the rows behind it
    x.lineTo(bx + bw, by + bh);
    x.lineTo(bx, by + bh);
    x.fillStyle = ground;
    x.fill();
    x.beginPath();
    for (let i = 0; i <= cols; i++) {
      const px = bx + (i / cols) * bw;
      const py = base - at(px, base) * lift;
      if (i) x.lineTo(px, py);
      else x.moveTo(px, py);
    }
    x.strokeStyle = j === live ? "#fff" : ink;
    x.globalAlpha = j === live ? 1 : 0.85;
    x.stroke();
  }
  x.restore();
}

export function mountPlumb(canvas: HTMLCanvasElement, options: PlumbOptions): PlumbHandle {
  const opts = {
    src: options.src,
    fx: options.fx ?? 0.5,
    fy: options.fy ?? 0.5,
    nodes: options.nodes ?? 6,
    tempo: options.tempo ?? 0.25,
    maxWidth: options.maxWidth ?? 1440,
  };
  const tones = PALETTES[options.palette ?? "plumb"];
  const ctx = canvas.getContext("2d")!;
  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const pic = document.createElement("canvas");
  const normal = document.createElement("canvas");
  const glyphs = document.createElement("canvas");
  const blocks = document.createElement("canvas");
  const depths = document.createElement("canvas");
  const edges = document.createElement("canvas");
  const tmp = document.createElement("canvas");

  let lockMode: Mode | null = options.mode && options.mode !== "auto" ? options.mode : null;
  let lockOv: Overlay | "none" | null = options.overlay && options.overlay !== "auto" ? options.overlay : null;
  let img: HTMLImageElement | null = null;
  let depthImg: HTMLImageElement | null = null;
  let scan: Frame = { w: 1, h: 1, lum: new Float32Array(1) };
  let W = 0;
  let H = 0;
  let u = 1;
  let cells: number[] = [];
  let gRows = 1;
  let mode: Mode = lockMode ?? "photo";
  let ov: Overlay = lockOv && lockOv !== "none" ? lockOv : "tracery";
  let hold = 40;
  let glitch = 0;
  let seed = 0;
  let simT = 0;
  let raf = 0;
  let last = 0;
  let visible = false;
  let dead = false;
  let pointer: [number, number] | null = null;
  const nodes: Node[] = Array.from({ length: opts.nodes }, (_, i) => ({
    i,
    x: 0.5,
    y: 0.5,
    bw: 0.1,
    bh: 0.1,
    on: false,
    life: i * 3,
    wl: 0,
    word: "",
  }));

  function build() {
    const rect = canvas.getBoundingClientRect();
    if (!img || rect.width < 2 || rect.height < 2) return;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    W = Math.round(Math.min(rect.width * scale, opts.maxWidth));
    H = Math.round((W * rect.height) / rect.width);
    u = (W / rect.width) * 0.85;
    for (const c of [canvas, pic, glyphs, blocks, tmp]) {
      c.width = W;
      c.height = H;
    }

    const pctx = pic.getContext("2d")!;
    const fit = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    const dw = img.naturalWidth * fit;
    const dh = img.naturalHeight * fit;
    pctx.drawImage(img, (W - dw) * opts.fx, (H - dh) * opts.fy, dw, dh);

    const nw = Math.min(W, 560);
    const nh = Math.max(2, Math.round((nw * H) / W));
    normal.width = nw;
    normal.height = nh;
    const relief = frameOf(depthImg ?? img, nw, nh, opts.fx, opts.fy);
    normal.getContext("2d")!.putImageData(paint(relief, "normal"), 0, 0);

    const ew = Math.min(W, 900);
    const eh = Math.max(2, Math.round((ew * H) / W));
    depths.width = ew;
    depths.height = eh;
    depths
      .getContext("2d")!
      .putImageData(depthMap(frameOf(depthImg ?? img, ew, eh, opts.fx, opts.fy), tones.glyphGround, tones.glyph), 0, 0);
    edges.width = ew;
    edges.height = eh;
    edges
      .getContext("2d")!
      .putImageData(edgeMap(frameOf(img, ew, eh, opts.fx, opts.fy), tones.glyphGround, tones.glyph), 0, 0);

    // The subject is whatever differs from the picture's border.
    gRows = Math.max(1, Math.round((GN * H) / W));
    const aw = GN * 4;
    const ah = gRows * 4;
    scan = frameOf(img, aw, ah, opts.fx, opts.fy);
    const { lum } = scan;
    const edge: number[] = [];
    for (let x = 0; x < aw; x++) edge.push(lum[x], lum[(ah - 1) * aw + x]);
    for (let y = 0; y < ah; y++) edge.push(lum[y * aw], lum[y * aw + aw - 1]);
    edge.sort((a, b) => a - b);
    const ground = edge[edge.length >> 1];
    const cover = (x0: number, y0: number, x1: number, y1: number) => {
      let sum = 0;
      let n = 0;
      for (let y = Math.floor(y0); y < y1; y++)
        for (let x = Math.floor(x0); x < x1; x++) {
          sum += Math.abs(lum[y * aw + x] - ground) > 0.16 ? 1 : 0;
          n++;
        }
      return n ? sum / n : 0;
    };

    cells = [];
    const g = glyphs.getContext("2d")!;
    const cs = W / GN;
    g.fillStyle = tones.glyphGround;
    g.fillRect(0, 0, W, H);
    g.strokeStyle = g.fillStyle = tones.glyph;
    g.lineWidth = Math.max(1, cs * 0.09);
    for (let gy = 0; gy < gRows; gy++)
      for (let gx = 0; gx < GN; gx++) {
        const c = cover(gx * 4, gy * 4, gx * 4 + 4, gy * 4 + 4);
        if (c > 0.5) cells.push(gy * GN + gx);
        const k = c > 0.5 ? 0 : c > 0.22 && rnd() < 0.08 ? 1 + ((rnd() * 7) | 0) : -1;
        if (k >= 0) glyph(g, k, gx * cs, gy * cs, cs);
      }

    const b = blocks.getContext("2d")!;
    const ts = W / TN;
    const tRows = Math.ceil(H / ts);
    b.fillStyle = tones.blockGround;
    b.fillRect(0, 0, W, H);
    for (let ty = 0; ty < tRows; ty++)
      for (let tx = 0; tx < TN; tx++) {
        const c = cover(
          (tx * aw) / TN,
          Math.min(ah - 1, (ty * aw) / TN),
          ((tx + 1) * aw) / TN,
          Math.min(ah, ((ty + 1) * aw) / TN)
        );
        if (c > 0.5) {
          b.fillStyle = tones.block;
          b.fillRect(Math.floor(tx * ts), Math.floor(ty * ts), Math.ceil(ts), Math.ceil(ts));
        } else if (c > 0.12) {
          b.strokeStyle = tones.blockEdge;
          b.lineWidth = Math.max(1, u);
          b.strokeRect(Math.floor(tx * ts) + 0.5, Math.floor(ty * ts) + 0.5, Math.ceil(ts) - 1, Math.ceil(ts) - 1);
        }
      }
  }

  function reseed(n: Node) {
    n.life = 15 + rnd() * 70;
    if (cells.length < 4) {
      n.on = false;
      n.life = 4;
      return;
    }
    n.on = rnd() < 0.85;
    const side = Math.min(W, H);
    n.bw = (clamp(0.07 + rnd() * 0.24, 0.05, 0.32) * side) / W;
    n.bh = (clamp(0.07 + rnd() * 0.24, 0.05, 0.32) * side) / H;
    let best = -1;
    for (let k = 0; k < 5; k++) {
      const i = pick(cells);
      const x = ((i % GN) + 0.5) / GN;
      const y = (((i / GN) | 0) + 0.5) / gRows;
      let d = 9;
      for (const m of nodes) if (m !== n && m.on) d = Math.min(d, Math.hypot(m.x - x, m.y - y));
      if (d > best) {
        best = d;
        n.x = x;
        n.y = y;
      }
    }
  }

  function step() {
    for (const n of nodes) {
      if (--n.wl <= 0) {
        n.word = pick(WORDS);
        n.wl = 10 + rnd() * 45;
      }
      if (--n.life <= 0) reseed(n);
    }
    if (pointer) {
      const n = nodes[0];
      n.on = true;
      n.life = 20;
      n.x += (pointer[0] - n.x) * 0.3;
      n.y += (pointer[1] - n.y) * 0.3;
    }
    glitch *= 0.62;
    if (--hold > 0) return;
    const r = rnd();
    const cut = () => {
      glitch = 0.25 + rnd() * 0.5;
      seed = rnd();
    };
    if (lockMode) {
      mode = lockMode;
      hold = (30 + rnd() * 60) / opts.tempo / 4;
      if (!lockOv) {
        ov = pick(OVERLAYS);
        if (mode === "photo") cut();
      }
      return;
    }
    mode =
      mode === "photo"
        ? pick<Mode>(["glyph", "block", "depth", "depth", "edge"])
        : r < 0.45
          ? "photo"
          : pick((["glyph", "block", "depth", "edge"] as Mode[]).filter(m => m !== mode));
    hold = mode === "photo" ? (3 + Math.pow(rnd(), 1.5) * 40) / opts.tempo : 9 + (rnd() * 8) / Math.sqrt(opts.tempo);
    if (mode === "photo" && !lockOv)
      ov = pick<Overlay>(["tracery", "tracery", "tracery", "detect", "detect", "fields", "fields", "windows"]);
    if (rnd() < 0.4) cut();
  }

  const regions = (): Region[] =>
    nodes
      .filter(n => n.on)
      .map(n => ({
        id: n.i + 1,
        label: n.word,
        cx: n.x,
        cy: n.y,
        x: n.x - n.bw / 2,
        y: n.y - n.bh / 2,
        w: n.bw,
        h: n.bh,
      }));

  function render() {
    if (!W) return;
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    const R = mode === "glyph" ? [] : regions();
    const style = lockOv === "none" ? null : ov;
    if (mode === "photo") {
      ctx.drawImage(pic, 0, 0);
      if (style === "fields")
        for (const r of R) {
          const x = clamp(r.x, 0, 1);
          const y = clamp(r.y, 0, 1);
          const w = Math.min(r.w, 1 - x);
          const h = Math.min(r.h, 1 - y);
          ctx.drawImage(
            normal,
            x * normal.width,
            y * normal.height,
            w * normal.width,
            h * normal.height,
            x * W,
            y * H,
            w * W,
            h * H
          );
        }
      if (style === "windows")
        for (const r of R) drawScan(ctx, r, W, H, u, simT, scan, tones.scanGround, tones.scan);
    } else if (mode === "depth") ctx.drawImage(depths, 0, 0, W, H);
    else if (mode === "edge") ctx.drawImage(edges, 0, 0, W, H);
    else ctx.drawImage(mode === "glyph" ? glyphs : blocks, 0, 0);

    if (mode === "block") drawTracery(ctx, R, W, H, u, tones.blockInk);
    else if (mode === "depth") {
      if (style) drawTracery(ctx, R, W, H, u, "#ffffff");
    } else if (mode === "edge") {
      if (style) drawDetect(ctx, R, W, H, u, simT);
    }
    else if (mode === "photo") {
      if (style === "tracery") drawTracery(ctx, R, W, H, u, "#fff6e0");
      else if (style === "detect") drawDetect(ctx, R, W, H, u, simT);
      else if (style === "fields") drawFields(ctx, R, W, H, u);
      else if (style === "windows") drawWindows(ctx, R, W, H, u, simT);
    }
    ctx.globalAlpha = 1;

    if (glitch > 0.01) {
      // Bands of the frame slide sideways for a few ticks after a cut.
      tmp.getContext("2d")!.drawImage(canvas, 0, 0);
      const bands = Math.floor(6 + 18 * hash(1, seed));
      const bh = Math.ceil(H / bands);
      for (let band = 0; band < bands; band++) {
        if (hash(band + 3, seed) <= 0.55) continue;
        const dx = Math.round((hash(band + 9, seed) - 0.5) * glitch * W);
        const y = band * bh;
        ctx.drawImage(tmp, 0, y, W, bh, dx, y, W, bh);
        ctx.drawImage(tmp, 0, y, W, bh, dx + (dx > 0 ? -W : W), y, W, bh);
      }
    }
  }

  function frame(now: number) {
    raf = requestAnimationFrame(frame);
    if (now - last < 1000 / HZ) return;
    last = now;
    simT += 1 / HZ;
    step();
    render();
  }

  function run() {
    cancelAnimationFrame(raf);
    if (dead || !visible || !W) return;
    if (still) {
      for (let i = 0; i < 40; i++) step();
      mode = lockMode ?? "photo";
      glitch = 0;
      render();
    } else raf = requestAnimationFrame(frame);
  }

  const io = new IntersectionObserver(
    ([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !W) build();
      run();
    },
    { rootMargin: "120px" }
  );
  const ro = new ResizeObserver(() => {
    if (!visible) return;
    build();
    run();
  });
  const onMove = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    pointer = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };
  const onLeave = () => {
    pointer = null;
  };
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerleave", onLeave);

  const depthLoad = options.depth ? loadImage(options.depth).catch(() => null) : Promise.resolve(null);
  Promise.all([loadImage(opts.src), depthLoad]).then(([loaded, depth]) => {
    if (dead) return;
    img = loaded;
    depthImg = depth;
    io.observe(canvas);
    ro.observe(canvas);
  });

  return {
    set(patch) {
      if (patch.mode !== undefined) lockMode = patch.mode === "auto" ? null : patch.mode;
      if (patch.overlay !== undefined) lockOv = patch.overlay === "auto" ? null : patch.overlay;
      if (lockMode) mode = lockMode;
      if (lockOv && lockOv !== "none") ov = lockOv;
      hold = 1;
      if (still) run();
    },
    destroy() {
      dead = true;
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
    },
  };
}
